import { env } from "@/env";
import { artefactKeys } from "@features/artefacts/services/queryKeys";
import { sessionKeys } from "@features/history/hooks/useSessions";
import { requirementTestsKeys } from "@features/requirement-tests/services/queryKeys";
import { requirementKeys } from "@features/requirements/services/queryKeys";
import { workbenchV2Keys } from "@features/workbench-v2/services/queryKeys";
import { useChatSurface } from "@layouts/AppLayout/useLayoutMode";
import type { ChatMessage } from "@models/chat";
import { useChatStore } from "@stores/chatStore";
import { useOrgStore } from "@stores/orgStore";
import { useProjectStore } from "@stores/projectStore";
import { toast } from "@stores/toastStore";
import { useQueryClient } from "@tanstack/react-query";
import { isBomContent } from "@utils/bomParse";
import { extractMermaid, normalizeMessages } from "@utils/mermaidExtract";
import { useCallback, useRef } from "react";
import { sessionsApi } from "../services/api";
import { cancelReattach } from "./chatReattach";
import { _projectActivityEvent, recoverFromStreamDrop } from "./chatRecovery";
import { readSseEvents } from "./chatSseStream";
import { chatAbort, chatStop } from "./chatTurnRefs";
import { useLongTurnStatus } from "./useLongTurnStatus";
import { useNavigateChat } from "./useNavigateChat";

/**
 * The chat turn pipeline — owns `sendMessage` (POST /chat → SSE loop
 * → finalise / recover) and `stopStreaming` (cancel everything).
 *
 * Separated from `useSession` so the streaming pipeline (with its
 * idle watchdog, abort coordination, structured-error parsing, and
 * recover-on-drop fallback) can be reasoned about and tested
 * independently of session-switching gestures.
 */
export interface ChatStreamApi {
  sendMessage: (userText: string) => Promise<void>;
  stopStreaming: () => void;
}

export function useChatStream(): ChatStreamApi {
  const qc = useQueryClient();
  const navigateChat = useNavigateChat();
  const longTurnStatus = useLongTurnStatus();

  // Surface drives backend agent selection. We mirror it through a ref so
  // `sendMessage`'s closure (which only captures stable refs) reads the
  // value at click time, not at hook-mount time. Important on Requirements
  // because the user may toggle between Workbench and Requirements within
  // a single mounted CopilotRail.
  const surface = useChatSurface();
  const surfaceRef = useRef(surface);
  surfaceRef.current = surface;

  const sendMessage = useCallback(
    async (userText: string) => {
      // Snapshot store actions + current values from getState() rather
      // than closing over the hook scope. Actions are stable refs in
      // Zustand so this is identical to closure capture for them, but
      // for live values (sessionId, isStreaming, activeProject…) it
      // means we read whatever is true *at send time* instead of what
      // was true when the callback was last memoised — eliminates
      // stale-closure bugs and keeps the dep array tiny.
      const cs = useChatStore.getState();
      const ps = useProjectStore.getState();
      const os = useOrgStore.getState();
      if (cs.isStreaming || !userText.trim()) return;
      const activeProjectNow = ps.activeProject;
      const activeVersionIdNow = ps.activeVersionId;
      const activeOrgIdNow = os.activeOrgId;
      const selectedModelNow = cs.selectedModel;
      // Chat scope is owned by the chat store, not the project
      // store. The user picks it via the composer's scope chip
      // (`<ChatScopeChip />`) and it's independent from the
      // navigational `activeBoardId` (sidebar / URL). Empty list
      // means "whole project" — server treats `board_ids: []`
      // identically to omitted.
      const activeChatBoardIdsNow = cs.activeChatBoardIds;

      // ── 1. Flip the UI immediately ─────────────────────────────────
      // Push the user bubble, enter streaming state, and reset the
      // stream buffer *before* any network I/O so clicking a suggestion
      // chip transitions to the conversation view instantly instead of
      // feeling frozen while the session-create request is in flight.
      const userMsg: ChatMessage = {
        role: "user",
        content: userText,
        mermaid: [],
        is_bom: false,
        timestamp: new Date().toISOString(),
      };
      cs.addMessage(userMsg);
      chatStop.current = false;
      cs.setStreaming(true);
      cs.resetStreamingContent();
      // Sending again means the user has either topped up or accepted
      // the cost — clear any sticky out-of-credits banner from the
      // previous turn so it doesn't linger above the composer.
      cs.setInsufficientCredits(null);
      cs.pushActivity({ kind: "thinking", label: "Thinking…" });

      // Long-turn reassurance flashes (3 min threshold, 3 s flash, 1
      // min rotation) — extracted to its own hook.
      longTurnStatus.start();

      // ── 2. Ensure a session exists ─────────────────────────────────
      let currentSessionId = cs.sessionId;
      if (!currentSessionId) {
        try {
          const newSession = await sessionsApi.create({
            project_id: activeProjectNow ?? undefined,
            board_ids:
              activeChatBoardIdsNow.length > 0
                ? activeChatBoardIdsNow
                : undefined,
            version_id: activeVersionIdNow ?? undefined,
            organisation_id: activeOrgIdNow ?? undefined,
            // Pin the surface at session creation so this conversation
            // is tied to (project, surface) for life. The list endpoint
            // can then find it again when the user returns to this
            // surface, and switching to the other surface fetches a
            // different session entirely.
            surface: surfaceRef.current ?? undefined,
          });
          currentSessionId = newSession.session_id;
          useChatStore.getState().setSessionId(currentSessionId);
          // Push the new session id into the URL so a refresh mid-turn
          // (or after) lands back on the same conversation instead of
          // bouncing to the welcome screen.
          navigateChat({ s: currentSessionId });
          if (
            newSession.active_version_id &&
            newSession.active_version_id !== activeVersionIdNow
          ) {
            ps.setActiveVersion(
              newSession.active_version_id,
              newSession.active_version_number ?? null,
            );
          }
          void qc.invalidateQueries({ queryKey: sessionKeys.all });
        } catch {
          // Continue without a session ID — backend will create one.
        }
      }

      chatAbort.current = new AbortController();

      // Hoisted outside the try/catch so the catch block can reach it
      // when the stream errors AFTER we received the run_id event but
      // BEFORE the agent finished (e.g. proxy 502 mid-turn).
      let capturedRunId: string | null = null;
      let fullContent = "";
      let doneReceived = false;

      // ── Idle watchdog ───────────────────────────────────────────────
      // Backend pings the SSE every 20 s and the heartbeat-loop ticks
      // the run row every 5 s. A 60 s no-progress window is well above
      // both, so under healthy operation the watchdog never fires; once
      // it does, the connection is genuinely wedged (proxy timeout
      // mid-stream, backend SIGKILL, OS-level network hang). Aborting
      // funnels into the existing catch path which runs
      // `recoverFromStreamDrop` — that's where the user-facing error
      // gets sourced from the now-correct `/run-status`.
      const SSE_IDLE_TIMEOUT_MS = 60_000;
      let idleAborted = false;
      let idleTimer: ReturnType<typeof setTimeout> | null = null;
      const resetIdle = () => {
        if (idleTimer !== null) clearTimeout(idleTimer);
        idleTimer = setTimeout(() => {
          idleAborted = true;
          chatAbort.current?.abort();
        }, SSE_IDLE_TIMEOUT_MS);
      };
      const clearIdle = () => {
        if (idleTimer !== null) {
          clearTimeout(idleTimer);
          idleTimer = null;
        }
      };

      try {
        const response = await fetch(`${env.VITE_API_BASE_URL}/chat`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "text/event-stream",
          },
          body: JSON.stringify({
            message: userText,
            project_id: activeProjectNow,
            // Subset of Board UUIDs the user scoped this chat to,
            // owned by `useChatStore.activeChatBoardIds`. Empty
            // list = whole project; server treats it identically
            // to an omitted field. See docs/project-board-model.md
            // §Agent context.
            board_ids:
              activeChatBoardIdsNow.length > 0
                ? activeChatBoardIdsNow
                : undefined,
            session_id: currentSessionId,
            organisation_id: activeOrgIdNow,
            // Backend pins the chosen model on the ChatSession at first
            // turn and ignores this field on subsequent turns. The
            // ChatInput picker is locked once messages.length > 0 so
            // the value sent here is meaningful only on the first
            // message of a conversation.
            model: selectedModelNow,
            // Drives backend agent selection. Pinned on the session at
            // first turn so a Requirements-started conversation keeps
            // the requirements assistant even if the user switches to
            // Workbench mid-session. `null` (no Copilot surface
            // matched, e.g. dedicated /chat page) defaults to
            // workbench server-side.
            surface: surfaceRef.current ?? undefined,
          }),
          signal: chatAbort.current.signal,
        });

        if (!response.ok) {
          const err = await response
            .json()
            .catch(() => ({ detail: "Request failed" }));
          throw new Error(
            (err as { detail?: string }).detail ?? "Request failed",
          );
        }

        // Read the new session id from response header if provided.
        const newSessionId = response.headers.get("X-Session-Id");
        if (newSessionId && newSessionId !== currentSessionId) {
          useChatStore.getState().setSessionId(newSessionId);
          currentSessionId = newSessionId;
          navigateChat({ s: newSessionId });
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        // Arm the watchdog as soon as we own the reader. `onProgress`
        // fires on every reader.read() resolution — including SSE
        // comment heartbeats the parser drops — so the watchdog only
        // trips when the wire genuinely goes silent.
        resetIdle();

        for await (const ev of readSseEvents(reader, {
          onProgress: resetIdle,
        })) {
          if (ev.type === "done") {
            doneReceived = true;
            break;
          }
          if (ev.type === "error") {
            // Structured-error contract: the backend emits a JSON payload
            // as the error content with `{reason, message, exc_type}` (and
            // extra fields for specific reasons). Pattern-match on `reason`
            // for the cases that need bespoke UI; everything else falls
            // through to the generic banner.
            const parsed = _parseStructuredError(ev.content);
            if (parsed?.reason === "insufficient_credits") {
              useChatStore.getState().setInsufficientCredits({
                required: parsed.required ?? 0,
                available: parsed.available ?? 0,
              });
              toast.warning("Out of credits — top up to continue.");
              // Don't throw — the user can recover in-place. Treat the
              // turn as gracefully cancelled.
              break;
            }
            // For the typed agent-loop reasons, surface the message via the
            // existing banner pathway; the throw carries `__agentError` so
            // upstream Error boundaries / toasts know it's expected.
            const errorMessage = parsed?.message ?? ev.content ?? "Agent error";
            const err = new Error(errorMessage) as Error & {
              __agentError?: true;
              __errorReason?: string;
            };
            err.__agentError = true;
            if (parsed?.reason) {
              err.__errorReason = parsed.reason;
            }
            throw err;
          }
          if (ev.type === "run_id" && ev.content) {
            capturedRunId = ev.content;
            continue;
          }
          if (ev.type === "token" && ev.content) {
            useChatStore.getState().appendStreamChunk(ev.content);
            fullContent += ev.content;
            continue;
          }
          if (ev.type === "title" && ev.content) {
            useChatStore.getState().setSessionTitle(ev.content);
            if (currentSessionId) {
              void qc.invalidateQueries({ queryKey: sessionKeys.all });
            }
            continue;
          }
          // thinking / tool_call / tool_result — projected the same
          // way as during reattach.
          _projectActivityEvent(
            ev,
            useChatStore.getState().pushActivity,
            useChatStore.getState().pushLiveArtifact,
          );
        }

        // ── Stream ended — decide how to finalise ────────────────────
        // Happy path: we received `done`. Hydrate the message list
        // from the server instead of relying on the locally-
        // accumulated `fullContent`. The backend's persistence path
        // (sessions/service.py) joins ALL text parts from model events
        // regardless of tool-call presence, while the streaming path
        // routes some text to `thinking`-only events which never land
        // in `fullContent`. Fetching from the server after every turn
        // guarantees the committed message contains exactly what the
        // agent produced — no dropped paragraphs when the model emits
        // response prose in the same event as a function_call.
        //
        // Drop path: stream closed before `done`. The agent keeps
        // running server-side and writes every event to Neo4j. Run
        // recovery to find out and reattach if so.
        if (doneReceived && currentSessionId) {
          try {
            const page = await sessionsApi.getMessages(currentSessionId);
            useChatStore
              .getState()
              .setMessages(normalizeMessages(page.messages), {
                hasMoreOlder: page.has_more,
                oldestTimestamp: page.oldest_timestamp,
              });
          } catch {
            // Server unreachable right after `done` — fall back to the
            // locally-streamed content so the user still sees something.
            // Content may be partial if any text was routed only to
            // `thinking` events; refreshing later will hydrate the full
            // persisted version.
            if (fullContent) {
              const { text, mermaid } = extractMermaid(fullContent);
              const is_bom = isBomContent(text);
              useChatStore.getState().addMessage({
                role: "assistant",
                content: text,
                mermaid,
                is_bom,
                timestamp: new Date().toISOString(),
              });
            }
          }
        } else if (currentSessionId) {
          // Stream dropped before `done` — ask the server what
          // happened (running / completed / failed / killed) and
          // reattach if the run is still alive.
          const cur = useChatStore.getState();
          const result = await recoverFromStreamDrop({
            sessionId: currentSessionId,
            capturedRunId,
            setMessages: cur.setMessages,
            pushActivity: cur.pushActivity,
            pushLiveArtifact: cur.pushLiveArtifact,
            stopSignal: chatStop,
          });
          if (!result.recovered && !chatStop.current) {
            useChatStore.getState().addMessage({
              role: "assistant",
              content:
                result.errorMessage ||
                "The connection was interrupted while the assistant was " +
                  "working. Your messages are saved — please resend your " +
                  "last message to continue.",
              mermaid: [],
              is_bom: false,
              timestamp: new Date().toISOString(),
            });
          }
        }

        void qc.invalidateQueries({ queryKey: sessionKeys.all });
      } catch (error: unknown) {
        // Three kinds of AbortError are possible:
        //   1. User clicked Stop — `chatStop.current === true`. Bail
        //      silently; the user already gets visual confirmation
        //      from the input flipping out of the Stop state.
        //   2. Idle watchdog fired — `idleAborted === true`. Fall
        //      through to the recovery path below; the connection is
        //      wedged and we want the user to see what happened.
        //   3. React unmount / route away — neither flag set. Same as
        //      a user stop: silent bail.
        if (error instanceof Error && error.name === "AbortError") {
          if (!idleAborted) return;
          // else fall through to the recovery path with idleAborted=true.
        }

        // Network error, fetch failure, or agent-emitted error event.
        // The agent may still be running server-side — ask the
        // run-status endpoint and reattach if so before falling back to
        // an error banner. If the agent itself emitted an explicit
        // error event, that error becomes the user-facing message;
        // otherwise we let the recovery flow surface whatever the
        // server reports.
        let recovered = false;
        let recoveryMessage: string | undefined;
        if (currentSessionId) {
          const cur = useChatStore.getState();
          const result = await recoverFromStreamDrop({
            sessionId: currentSessionId,
            capturedRunId,
            setMessages: cur.setMessages,
            pushActivity: cur.pushActivity,
            pushLiveArtifact: cur.pushLiveArtifact,
            stopSignal: chatStop,
          });
          recovered = result.recovered;
          recoveryMessage = result.errorMessage;
        }

        if (!recovered) {
          // Pick the user-facing message in priority order:
          //   1. recoveryMessage — the server's verdict on the run
          //      (e.g. "The server restarted while the assistant was
          //      working." for `killed`).
          //   2. idle-watchdog fallback — when the wire went silent
          //      and recovery couldn't reach the server either.
          //   3. raw fetch error — for network errors / 5xx that
          //      didn't go through recovery cleanly.
          const idleFallback =
            "The connection stalled — no response from the agent for a " +
            "minute. Your messages are saved — please retry.";
          const fallback = idleAborted
            ? idleFallback
            : error instanceof Error
              ? error.message
              : "Something went wrong. Please try again.";
          const errorMsg: ChatMessage = {
            role: "assistant",
            content: recoveryMessage || fallback,
            mermaid: [],
            is_bom: false,
            timestamp: new Date().toISOString(),
          };
          useChatStore.getState().addMessage(errorMsg);
        }
        void qc.invalidateQueries({ queryKey: sessionKeys.all });
      } finally {
        clearIdle();
        longTurnStatus.stop();
        const fin = useChatStore.getState();
        fin.setStreaming(false);
        fin.resetStreamingContent();
        chatAbort.current = null;
        // Some chat tools (today: SPICE simulations) persist a
        // `workbench_artefact` row inline. Invalidate the artefacts
        // cache at every turn boundary so a sim run from the
        // CopilotRail surfaces in the Workbench tab strip without
        // requiring a manual refresh. Cheap when nothing changed —
        // React Query refetches only the queries currently mounted,
        // and the polling interval on `useArtefacts` already kicks
        // in for any in-flight rows.
        void qc.invalidateQueries({ queryKey: artefactKeys.all });
        // Same logic for the verification matrix: `propose_tests`
        // writes new entries to `Requirement.body.tests` from the chat
        // tool path. Without this, the matrix's per-requirement test
        // queries stay stale until the next page nav.
        void qc.invalidateQueries({ queryKey: requirementTestsKeys.all });
        // Same again for the workbench_v2 surface: `propose_test`,
        // `derive_plan`, `run_test`, `update_test_inputs`,
        // `submit_measurement`, `record_measurement`,
        // `edit_outcome_target`, and `add_input_to_test` all mutate
        // the wb_test* tables via chat. Invalidating the workbench v2
        // root key blows away every cached list / detail / runs query
        // under it so the next render fetches fresh state.
        void qc.invalidateQueries({ queryKey: workbenchV2Keys.all });
        // And for the requirements list itself: `propose_requirement`
        // writes a suggested row from the agent path. Without this,
        // the Requirements tab only refetches on window focus, so the
        // user sometimes sees their new suggestions and sometimes
        // has to click around to surface them.
        void qc.invalidateQueries({ queryKey: requirementKeys.all });
      }
    },
    [navigateChat, qc, longTurnStatus],
  );

  const stopStreaming = useCallback(() => {
    // Force every layer of the turn pipeline to shut down:
    //   1. chatStop — polling loop checks this on every iteration and
    //      returns false immediately when set.
    //   2. chatAbort — aborts the fetch/ReadableStream for the SSE
    //      layer, so a stop during streaming rejects reader.read() with
    //      AbortError (handled in the outer catch with an early
    //      return).
    //   3. cancelReattach — if Stop is clicked during the reconnect
    //      handshake, the reattach loop owns its own stop signal and
    //      its finally block deliberately skips clearing the flags
    //      when cancelled, expecting the caller to clean up.
    //   4. Immediate UI state reset — don't wait for the async pipeline
    //      to unwind before the streaming indicator disappears.
    chatStop.current = true;
    chatAbort.current?.abort();
    cancelReattach();
    const st = useChatStore.getState();
    st.setStreaming(false);
    st.setReconnecting(false);
    st.resetStreamingContent();
    st.setTransientStatus(null);
  }, []);

  return { sendMessage, stopStreaming };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

interface StructuredErrorPayload {
  reason?: string;
  /** Used by the `insufficient_credits` path; ignored elsewhere. */
  required?: number;
  /** Used by the `insufficient_credits` path; ignored elsewhere. */
  available?: number;
  /** User-facing string the backend formatted for this `reason`. */
  message?: string;
  /** Underlying exception class — diagnostic only. */
  exc_type?: string;
}

/**
 * Try to coerce an SSE error `content` string into a structured payload.
 * Returns `null` for plain-text errors so the caller can fall through to
 * the legacy throw path. Defensive: never throws on malformed input.
 */
function _parseStructuredError(
  content: string | undefined,
): StructuredErrorPayload | null {
  if (!content) return null;
  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const obj = JSON.parse(trimmed) as unknown;
    if (obj && typeof obj === "object" && "reason" in obj) {
      return obj as StructuredErrorPayload;
    }
    return null;
  } catch {
    return null;
  }
}
